import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import cmsContentBlockAssignmentObject from '@/entities/cmsContentBlockAssignment/interface'

import {
  ALL_BACKGROUND_BLOCKS_QUERY,
  SINGLE_BACKGROUND_BLOCK_QUERY,
  CREATE_BACKGROUND_BLOCK_MUTATION,
  UPDATE_BACKGROUND_BLOCK_MUTATION,
  DELETE_BACKGROUND_BLOCK_MUTATION,
  FORCE_DELETE_BACKGROUND_BLOCK_MUTATION,
  ADD_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  REMOVE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  CREATE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  DELETE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION,
  UPDATE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsBackgroundBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_BACKGROUND_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.backgroundBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_BACKGROUND_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.backgroundBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_BACKGROUND_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.backgroundBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_BACKGROUND_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.backgroundBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_BACKGROUND_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.backgroundBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_BACKGROUND_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.backgroundBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_BACKGROUND_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsBackgroundBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_BACKGROUND_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsBackgroundBlock
    })
  },

  assignContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  unassignContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  createContentBlockAssignments: async (id: string, items: Array<cmsContentBlockAssignmentObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignments: items,
      },
    }
    return graphQLClient.request(CREATE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  deleteContentBlockAssignments: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_MUTATION, variables)
  },

  updateContentBlockAssignmentsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        contentBlockAssignmentIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_BACKGROUND_BLOCK_CONTENT_BLOCK_ASSIGNMENTS_ORDER_MUTATION, variables)
  },
}

export default cmsBackgroundBlockRepository
